import { Module } from 'vuex';
import { apiService } from '@/services/api.service';

export interface CalendarActivity {
  id: string;
  title: string;
  description: string;
  startDate: string;
  endDate: string;
  location: string;
  type: 'national' | 'international' | 'regional' | 'local';
  status: 'active' | 'postponed' | 'cancelled' | 'completed';
  createdAt?: string;
  updatedAt?: string;
}

interface CalendarState {
  activities: CalendarActivity[];
  availableYears: number[];
  selectedYear: number | null;
  loading: boolean;
  yearsLoading: boolean;
  error: string | null;
}

const calendarModule: Module<CalendarState, any> = {
  namespaced: true,

  state: {
    activities: [],
    availableYears: [],
    selectedYear: null,
    loading: false,
    yearsLoading: false,
    error: null,
  },

  getters: {
    getActivities: (state) => state.activities,
    getAvailableYears: (state) => state.availableYears,
    getSelectedYear: (state) => state.selectedYear,
    isLoading: (state) => state.loading,
    isYearsLoading: (state) => state.yearsLoading,
    getError: (state) => state.error,
    
    getActivityById: (state) => (id: string) => {
      return state.activities.find((activity) => activity.id === id);
    },

    getActiveActivities: (state) => {
      return state.activities.filter((activity) => activity.status === 'active');
    },

    getActivitiesByType: (state) => (type: string) => {
      return state.activities.filter((activity) => activity.type === type);
    },

    getUpcomingActivities: (state) => {
      const now = new Date();
      return state.activities
        .filter((activity) => new Date(activity.startDate) > now)
        .sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime());
    },

    getActivitiesByDateRange: (state) => (startDate: string, endDate: string) => {
      const start = new Date(startDate);
      const end = new Date(endDate);
      
      return state.activities.filter((activity) => {
        const activityStart = new Date(activity.startDate);
        const activityEnd = new Date(activity.endDate);
        
        return (
          (activityStart >= start && activityStart <= end) ||
          (activityEnd >= start && activityEnd <= end) ||
          (activityStart <= start && activityEnd >= end)
        );
      });
    },
  },

  mutations: {
    SET_ACTIVITIES(state, activities: CalendarActivity[]) {
      state.activities = activities;
    },

    ADD_ACTIVITY(state, activity: CalendarActivity) {
      state.activities.push(activity);
    },

    UPDATE_ACTIVITY(state, updatedActivity: CalendarActivity) {
      const index = state.activities.findIndex((a) => a.id === updatedActivity.id);
      if (index !== -1) {
        state.activities.splice(index, 1, updatedActivity);
      }
    },

    REMOVE_ACTIVITY(state, activityId: string) {
      state.activities = state.activities.filter((a) => a.id !== activityId);
    },

    SET_LOADING(state, loading: boolean) {
      state.loading = loading;
    },

    SET_ERROR(state, error: string | null) {
      state.error = error;
    },

    CLEAR_ACTIVITIES(state) {
      state.activities = [];
    },

    SET_YEARS(state, years: number[]) {
      state.availableYears = years;
    },

    SET_SELECTED_YEAR(state, year: number | null) {
      state.selectedYear = year;
    },

    SET_YEARS_LOADING(state, loading: boolean) {
      state.yearsLoading = loading;
    },
  },

  actions: {
    /**
     * Obtener los años disponibles en el calendario (endpoint público)
     */
    async fetchCalendarYears({ commit }) {
      commit('SET_YEARS_LOADING', true);
      try {
        const years = await apiService.getCalendarYears();
        commit('SET_YEARS', years);
        // Seleccionar automáticamente el año más reciente si hay disponibles
        if (years.length > 0) {
          commit('SET_SELECTED_YEAR', years[0]);
        }
        return years;
      } catch (error: any) {
        console.warn('No se pudieron cargar los años del calendario:', error.message);
        return [];
      } finally {
        commit('SET_YEARS_LOADING', false);
      }
    },

    /**
     * Obtener todas las actividades del calendario (Admin)
     */
    async fetchCalendarActivities({ commit }, payload?: { year?: number | null }) {
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);
      
      const year = payload?.year ?? null;

      try {
        const activities = await apiService.getCalendarActivities(year);
        commit('SET_ACTIVITIES', activities);
        return activities;
      } catch (error: any) {
        const errorMessage = error.message || 'Error al cargar las actividades del calendario';
        commit('SET_ERROR', errorMessage);
        console.error('Error fetching calendar activities:', error);
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    /**
     * Obtener actividades por mes (Público)
     */
    async fetchCalendarActivitiesByMonth({ commit }, { year, month }: { year: number; month: number }) {
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);
      
      try {
        const activities = await apiService.getCalendarActivitiesByMonth(year, month);
        commit('SET_ACTIVITIES', activities);
        return activities;
      } catch (error: any) {
        const errorMessage = error.message || 'Error al cargar las actividades del mes';
        commit('SET_ERROR', errorMessage);
        console.error('Error fetching calendar activities by month:', error);
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    /**
     * Obtener actividades por rango de fechas (Público)
     */
    async fetchCalendarActivitiesByRange({ commit }, { startDate, endDate }: { startDate: string; endDate: string }) {
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);
      
      try {
        const activities = await apiService.getCalendarActivitiesByRange(startDate, endDate);
        commit('SET_ACTIVITIES', activities);
        return activities;
      } catch (error: any) {
        const errorMessage = error.message || 'Error al cargar las actividades';
        commit('SET_ERROR', errorMessage);
        console.error('Error fetching calendar activities by range:', error);
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    /**
     * Obtener detalle de una actividad
     */
    async fetchCalendarActivity({ commit }, activityId: string) {
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);
      
      try {
        const activity = await apiService.getCalendarActivity(activityId);
        return activity;
      } catch (error: any) {
        const errorMessage = error.message || 'Error al cargar la actividad';
        commit('SET_ERROR', errorMessage);
        console.error('Error fetching calendar activity:', error);
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    /**
     * Crear una nueva actividad
     */
    async createCalendarActivity({ commit }, activityData: Omit<CalendarActivity, 'id' | 'createdAt' | 'updatedAt'>) {
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);
      
      try {
        const newActivity = await apiService.createCalendarActivity(activityData);
        commit('ADD_ACTIVITY', newActivity);
        return newActivity;
      } catch (error: any) {
        const errorMessage = error.message || 'Error al crear la actividad';
        commit('SET_ERROR', errorMessage);
        console.error('Error creating calendar activity:', error);
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    /**
     * Actualizar una actividad
     */
    async updateCalendarActivity({ commit }, { activityId, activityData }: { activityId: string; activityData: Partial<CalendarActivity> }) {
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);
      
      try {
        const updatedActivity = await apiService.updateCalendarActivity(activityId, activityData);
        commit('UPDATE_ACTIVITY', updatedActivity);
        return updatedActivity;
      } catch (error: any) {
        const errorMessage = error.message || 'Error al actualizar la actividad';
        commit('SET_ERROR', errorMessage);
        console.error('Error updating calendar activity:', error);
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    /**
     * Eliminar una actividad
     */
    async deleteCalendarActivity({ commit }, activityId: string) {
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);
      
      try {
        await apiService.deleteCalendarActivity(activityId);
        commit('REMOVE_ACTIVITY', activityId);
      } catch (error: any) {
        const errorMessage = error.message || 'Error al eliminar la actividad';
        commit('SET_ERROR', errorMessage);
        console.error('Error deleting calendar activity:', error);
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    /**
     * Limpiar actividades del estado
     */
    clearCalendarActivities({ commit }) {
      commit('CLEAR_ACTIVITIES');
      commit('SET_ERROR', null);
    },
  },
};

export default calendarModule;
